"use strict";

if (!customElements.get("text-overflow-toggle")) {
  customElements.define("text-overflow-toggle", class extends HTMLElement {
    static observedAttributes = ["disabled"];
    #input;
    #content;
    #label;
    #observer;
    #raf;
    #timer;
    #transitionHandler = null;
    #overflowing = false;
    #config;
    #mediaQuery;
    #currentBreakpoint;
    constructor() {
      super();
      this.#input = this.querySelector('input[type="checkbox"]');
      this.#content = this.#input?.nextElementSibling;
      this.#label = this.#content?.nextElementSibling;
      this.#config = {
        collapsedDesktop: parseInt(this.dataset.collapsedDesktop || "6", 10),
        collapsedMobile: parseInt(this.dataset.collapsedMobile || "4", 10),
        lineHeight: parseFloat(this.dataset.lineHeight || "1.4"),
        breakpoint: parseInt(this.dataset.breakpoint || "1024", 10)
      };
      this.#mediaQuery = window.matchMedia(`(min-width: ${this.#config.breakpoint}px)`);
      this.#currentBreakpoint = this.#mediaQuery.matches ? "desktop" : "mobile";
    }
    connectedCallback() {
      if (!this.#input || !this.#content || !this.#label) return;
      if (!getComputedStyle(this.#content).transition.includes("max-height")) {
        this.#content.style.transition = "max-height 300ms ease-out";
      }
      this.#input.addEventListener("change", () => this.#toggle());
      this.#mediaQuery.addEventListener("change", () => this.#handleBreakpoint());
      this.#updateLabel();
      this.#observe();
      this.#check();
    }
    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#mediaQuery?.removeEventListener("change", () => this.#handleBreakpoint());
      cancelAnimationFrame(this.#raf);
      clearTimeout(this.#timer);
      if (this.#transitionHandler) {
        this.#content.removeEventListener("transitionend", this.#transitionHandler);
      }
    }
    attributeChangedCallback() {
      this.#updateLabel();
    }
    #getCollapsedHeight() {
      const lines = this.#currentBreakpoint === "desktop" ? this.#config.collapsedDesktop : this.#config.collapsedMobile;
      const fontSize = parseFloat(getComputedStyle(this.#content).fontSize) || 16;
      return fontSize * this.#config.lineHeight * lines;
    }
    #handleBreakpoint() {
      const newBreakpoint = this.#mediaQuery.matches ? "desktop" : "mobile";
      if (newBreakpoint !== this.#currentBreakpoint) {
        this.#currentBreakpoint = newBreakpoint;
        if (!this.#input.checked) {
          this.#content.style.maxHeight = `${this.#getCollapsedHeight()}px`;
        }
        this.#check();
      }
    }
    #check() {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        const wasOverflowing = this.#overflowing;
        const {
          maxHeight,
          transition,
          webkitLineClamp
        } = this.#content.style;
        this.#content.style.transition = "none";
        this.#content.style.maxHeight = "none";
        this.#content.style.webkitLineClamp = "unset";
        const fullHeight = this.#content.scrollHeight;
        this.#content.style.webkitLineClamp = webkitLineClamp;
        this.#content.style.maxHeight = maxHeight;
        void this.#content.offsetHeight;
        this.#content.style.transition = transition;
        const collapsedHeight = this.#getCollapsedHeight();
        this.#overflowing = Math.round(fullHeight) > Math.round(collapsedHeight) + 1;
        if (wasOverflowing !== this.#overflowing) {
          this.#updateLabel();
          if (!this.#overflowing && this.#input.checked) {
            this.#input.checked = false;
            this.#toggle();
          }
        }
      });
    }
    #updateLabel() {
      this.#label.style.display = this.#overflowing ? "" : "none";
      const isInteractive = !this.hasAttribute("disabled");
      Object.assign(this.#label.style, {
        opacity: isInteractive ? "1" : "0",
        pointerEvents: isInteractive ? "auto" : "none"
      });
      this.#input.disabled = this.hasAttribute("disabled");
    }
    #toggle() {
      const expanding = this.#input.checked;
      const collapsedHeight = this.#getCollapsedHeight();
      if (this.#transitionHandler) {
        this.#content.removeEventListener("transitionend", this.#transitionHandler);
        this.#transitionHandler = null;
      }
      if (expanding) {
        this.#content.style.webkitLineClamp = "unset";
        const originalTransition = this.#content.style.transition;
        this.#content.style.transition = "none";
        this.#content.style.maxHeight = "none";
        const fullHeight = this.#content.scrollHeight;
        this.#content.style.maxHeight = `${collapsedHeight}px`;
        this.#content.offsetHeight;
        this.#content.style.transition = originalTransition;
        const duration = Math.min(600, Math.max(200, Math.sqrt(fullHeight) * 10));
        this.#content.style.transitionDuration = `${duration}ms`;
        requestAnimationFrame(() => {
          this.#content.style.maxHeight = `${fullHeight + 20}px`;
        });
        this.dispatchEvent(new CustomEvent("toggle", {
          detail: {
            expanded: true,
            height: fullHeight
          },
          bubbles: true
        }));
      } else {
        this.#content.style.webkitLineClamp = "unset";
        const currentHeight = this.#content.scrollHeight;
        const duration = Math.min(600, Math.max(200, Math.sqrt(currentHeight) * 10));
        this.#content.style.maxHeight = `${currentHeight}px`;
        this.#content.style.transitionDuration = `${duration}ms`;
        requestAnimationFrame(() => {
          this.#content.style.maxHeight = `${collapsedHeight}px`;
        });
        this.#transitionHandler = e => {
          if (e.propertyName === "max-height") {
            this.#content.style.webkitLineClamp = "";
            this.#content.removeEventListener("transitionend", this.#transitionHandler);
            this.#transitionHandler = null;
          }
        };
        this.#content.addEventListener("transitionend", this.#transitionHandler);
        this.dispatchEvent(new CustomEvent("toggle", {
          detail: {
            expanded: false,
            height: collapsedHeight
          },
          bubbles: true
        }));
      }
    }
    #observe() {
      let lastWidth = this.#content.offsetWidth;
      this.#observer = new ResizeObserver(() => {
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => {
          const newWidth = this.#content.offsetWidth;
          if (Math.abs(newWidth - lastWidth) > 1) {
            lastWidth = newWidth;
            if (!this.#input.checked) {
              this.#content.style.maxHeight = `${this.#getCollapsedHeight()}px`;
            }
            this.#check();
          }
        }, 16);
      });
      this.#observer.observe(this.#content);
    }
    toggle(expanded) {
      if (!this.hasAttribute("disabled")) {
        this.#input.checked = expanded ?? !this.#input.checked;
        this.#toggle();
      }
    }
    get expanded() {
      return this.#input.checked;
    }
    get overflowing() {
      return this.#overflowing;
    }
  });
}